'use client';

import { useMemo, useState } from 'react';
import { FlagIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import '@tranquility-ai/theme-components/menu.css';
import './media-flag-menu.css';

import { useCreateContentModerationFinding } from '@/hooks/useCreateContentModerationFinding';
import { useManualObfuscationJobStatus } from '@/hooks/useManualObfuscationJobStatus';
import { useCsamAccess } from '@/lib/csam-access';
import type { ManualObfuscationJobStatus } from '@/shared/cors-provenance-api';
import { mediaIconButtonProps } from './media-icon-button';
import { FlagCsamConfirmModal } from './flag-csam-confirm-modal';
import {
  flagSubmitErrorMessage,
  isImageMedia,
  resolveFlagMenuItems,
  shouldShowFlagControl,
} from './media-flag-rules';

export type MediaFlagMenuProps = {
  fileUrl?: string | null;
  caseId?: string | null;
  mediaType?: string | null;
  contentModerationType?: string | null;
  /** File-info status when Flag is clicked. REVIEWED marks a re-flag. */
  findingStatus?: string | null;
  /** Provenance blur-job status — ``IN_PROGRESS`` shows the Flag spinner. */
  manualObfuscationJobStatus?: ManualObfuscationJobStatus | null;
  /** True when the blur poll timed out or failed. Stops the Flag spinner. */
  blurFailed?: boolean;
  className?: string;
  /** Use pane-header icon sizing when rendered in fullscreen Pane controls. */
  compact?: boolean;
};

type MenuAction = 'csam';

function fileLabelFromUrl(fileUrl?: string | null): string {
  if (!fileUrl) return '';
  const cleaned = fileUrl.split('?')[0];
  const parts = cleaned.split('/');
  return parts[parts.length - 1] || cleaned;
}

/**
 * Flag button for the media toolbar (TR-3168). Candidate for promotion to
 * theme-components after the production write path ships.
 */
export function MediaFlagMenu({
  fileUrl,
  caseId,
  mediaType,
  contentModerationType,
  findingStatus,
  manualObfuscationJobStatus: jobStatusProp,
  blurFailed = false,
  compact = false,
}: MediaFlagMenuProps) {
  const { canFlagMedia } = useCsamAccess();
  const [open, setOpen] = useState(false);
  const [confirmCsam, setConfirmCsam] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const createMutation = useCreateContentModerationFinding();
  const resolvedJobStatus = useManualObfuscationJobStatus(fileUrl, jobStatusProp);

  const imageOk = isImageMedia(mediaType, fileUrl);
  const alreadyFlagged = Boolean(contentModerationType);
  const processing = resolvedJobStatus === 'IN_PROGRESS' && !blurFailed;

  const resolvedCaseId = (caseId || '').trim();
  const canSubmit = Boolean(fileUrl && resolvedCaseId);

  const items = useMemo(
    () => resolveFlagMenuItems({ alreadyFlagged, canSubmit }),
    [alreadyFlagged, canSubmit],
  );

  const handleSelect = (id: MenuAction) => {
    setOpen(false);
    if (id === 'csam') {
      setConfirmCsam(true);
    }
  };

  const handleConfirmCsam = async () => {
    if (!fileUrl || !resolvedCaseId) return;
    try {
      await createMutation.mutateAsync({
        caseId: resolvedCaseId,
        fileUrl,
        baselineFindingStatus: findingStatus,
      });
      setConfirmCsam(false);
    } catch (error) {
      setNotice(flagSubmitErrorMessage(error));
    }
  };

  if (!canFlagMedia || !imageOk || !shouldShowFlagControl(contentModerationType)) return null;

  return (
    <>
      <Menu
        isOpen={open}
        onOpenChange={setOpen}
        inverted={false}
        placement="bottom-end"
        showArrow={false}
        aria-label="Flag this file as CSAM"
        content={
          <>
            {items.map((item) => (
              <MenuItem
                key={item.id}
                disabled={item.disabled}
                title={item.disabled ? item.reason : undefined}
                data-testid={`media-flag-${item.id}`}
                onSelect={() => handleSelect(item.id)}
              >
                {item.label}
              </MenuItem>
            ))}
          </>
        }
      >
        <Button
          type="button"
          {...mediaIconButtonProps}
          aria-label={processing ? 'Creating safe preview' : 'Flag this file as CSAM'}
          aria-haspopup="menu"
          data-testid="media-flag-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((current) => !current);
          }}
        >
          {processing ? (
            <SpinnerGapIcon size={compact ? 16 : 20} />
          ) : (
            <FlagIcon size={compact ? 16 : 20} />
          )}
        </Button>
      </Menu>
      <FlagCsamConfirmModal
        isOpen={confirmCsam}
        fileLabel={fileLabelFromUrl(fileUrl)}
        isPending={createMutation.isPending}
        error={notice}
        onCancel={() => {
          setNotice(null);
          setConfirmCsam(false);
        }}
        onConfirm={() => void handleConfirmCsam()}
      />
    </>
  );
}
